import React, { useContext, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import '../../../css/Accessibility.scss';
import '../../../css/ChoiceMatrixMulti.scss';

import { LessonMode } from '../../../Constants';

import FeedbackIcon from '../FeedbackIcon';
import Guideline from '../tools/Guideline';
import HtmlComponent from '../HtmlComponent';
import OptionalImage from '../OptionalImage';
import PrintQuestionNumber from './PrintQuestionNumber';

import responseService from '../../services/ResponseService';
import utilsService from '../../services/UtilsService';

import { confirmDialog } from '../dialogs';

import useStyleEvents from '../../../hooks/useStyleEvents';

const getWidths = (model) => {
  let answerColumnWidths = [];
  if (model.answerColumnWidths === undefined) {
    if (model.options && model.options.length > 0) {
      for (let x = 0; x < model.options.length; x++) {
        answerColumnWidths.push('100');
      }
    }
    return answerColumnWidths;
  } else {
    answerColumnWidths = model.answerColumnWidths.map((w) => parseInt(w));
    return answerColumnWidths;
  }
};

const ChoiceMatrixMulti = observer(({ lessonElementId }) => {
  const {
    lessonManager,
    toolbarManager,
    responseManager,
    questionFeedbackManager
  } = useContext(MobXProviderContext);

  // Define state
  const model = lessonManager.getSlideModel(lessonElementId);
  const lessonElementState = responseManager.getLessonElementState(lessonElementId);
  const feedbackState = questionFeedbackManager.getUiState(lessonElementId);
  const { currentResponseAnswer } = lessonElementState;

  const readOnly = lessonElementState.readOnly || lessonManager.isReadOnly;
  const { showAnswers } = feedbackState;

  const textBody = model.questionText || '';
  const tableHeader = model.answerTextHeader; // In the top left box
  const isMultiChoice = (model.answerType === 'mchoice' || model.answerType === undefined); // If false then this is multiselect
  const labelInCell = (model.answerPosition === 'cells' || model.answerPosition === undefined); // If this is false then the labels are in the header
  const { fixedWidths } = model;
  const promptColumnWidth = parseInt(model.promptColumnWidth);
  const { prompts } = model; // On the left side
  const noPrompts = prompts.length === 1 && prompts[0].text.length === 0;
  const answerColumnWidths = getWidths(model);
  const answerOptions = model.options.filter((option) => option.length > 0); // Either in the header or in the cells
  const noHeader = (!tableHeader || tableHeader.length === 0) && (answerOptions.every((o) => o.length === 0) || labelInCell);

  const isCorrectOption = (promptId, answerIndex) => {
    const answerResults = currentResponseAnswer?.answerResults;
    if (answerResults && answerResults.length > 0) {
      const result = answerResults.find((r) => r.answerId === promptId);
      if (result !== undefined) {
        return Boolean(result.match);
      }
    }

    const correctAnswers = model.validation?.correctAnswers;
    if (!correctAnswers) {
      return false;
    }
    const promptIndex = prompts.findIndex((p) => p.id === promptId);
    if (correctAnswers[promptIndex]) {
      const correctAnswerIndices = correctAnswers[promptIndex].split(',').map((a) => parseInt(a));
      return correctAnswerIndices.includes(answerIndex);
    }
    return false;
  };

  const isSelectedOption = (promptId, answerIndex) => {
    const selectedAnswers = currentResponseAnswer.answers;
    const promptIndex = prompts.findIndex((p) => p.id === promptId);
    const selectedAnswerIndices = selectedAnswers[promptIndex].split(',').map((a) => parseInt(a));
    return selectedAnswerIndices.includes(answerIndex);
  };

  const onSelected = async (promptId, newState, answerIndex) => {
    const data = { promptId, answerIndex };

    if (readOnly) {
      return;
    }

    const currentlySelected = currentResponseAnswer.answers.reduce((count, item) => {
      const values = item.split(',');
      const numberValues = values.filter((value) => value !== '' && !isNaN(Number(value)));
      return count + numberValues.length;
    }, 0);

    if (model.correctAnswerCount && !model.allowSelectAll && !model.unscored && newState && currentlySelected >= model.correctAnswerCount && !isMultiChoice) {
      // Then we are trying to select an answer when we already have the maximum number of answers selected
      await confirmDialog({
        title: `${utilsService.maxSelected()}`,
        text: `${utilsService.deselect()}`,
        showCancelButton: false,
        confirmButtonText: `${utilsService.confirmOk()}`
      });
    } else {
      await responseService.responseChangeHandler(data, lessonElementId);
    }
  };

  useStyleEvents(lessonElementId);

  return (
    <div className='choice-matrix-multi-question'>
      {toolbarManager.isGuidelineOpen && <Guideline lessonElementId={lessonElementId} />}
      <div className='test-item-question'>
        {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && <PrintQuestionNumber model={model} />}
        <HtmlComponent htmlStr={textBody} />
      </div>
      <OptionalImage model={model} runtime={true} />
      <div className='test-item-answers'>
        <table className='choice-matrix-multi-table' style={{ width: fixedWidths ? 'auto' : '100%' }}>
          {!noHeader && tableHeader && (
            <caption className='sr-only'>
              <HtmlComponent htmlStr={tableHeader} />
            </caption>
          )}
          <thead>
            {!noHeader && (
              <tr>
                <th className='choice-matrix-header' scope='col' style={{ width: fixedWidths ? promptColumnWidth : 'auto' }}>
                  <HtmlComponent htmlStr={tableHeader} />
                </th>
                {answerOptions.map((answer, answerIndex) => (
                  <th
                    key={answerIndex}
                    className={`choice-matrix-header answer-header ${ labelInCell ? 'empty' : 'full' }`}
                    scope='col'
                    style={{ width: fixedWidths ? answerColumnWidths[answerIndex] : 'auto' }}>
                    <HtmlComponent htmlStr={labelInCell ? '' : answer} />
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {
              !noPrompts && prompts.map(({ id, text }) => (
                <RowContainer key={id}
                  isMultiChoice={isMultiChoice}
                  promptId={id}>
                  <th className='choice-matrix-cell' id={`${id}-label`} scope='row' style={{ width: fixedWidths ? promptColumnWidth : 'auto' }}>
                    <HtmlComponent htmlStr={text} />
                  </th>
                  {answerOptions.map((answer, answerIndex) => (
                    <ChoiceCell key={`${id}-${answerIndex}`}
                      answer={answer}
                      answerIndex={answerIndex}
                      isCorrectAnswer={isCorrectOption(id, answerIndex)}
                      isMultiChoice={isMultiChoice}
                      isSelected={isSelectedOption(id, answerIndex)}
                      labelInCell={labelInCell}
                      lessonElementId={lessonElementId}
                      lessonElementState={lessonElementState}
                      model={model}
                      onSelected={onSelected}
                      promptId={id}
                      readOnly={readOnly}
                      showAnswers={showAnswers}
                      showFeedbackOnlyOnSelected={Boolean(currentResponseAnswer?.answerResults?.length > 0 && currentResponseAnswer.answerResults.some((r) => r.answerId === id))}
                    />
                  ))}
                </RowContainer>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
});

const RowContainer = ({ isMultiChoice, promptId, children }) => {
  if (isMultiChoice) {
    // Then this should be a radio group
    return (
      <tr aria-labelledby={`${promptId}-label`} role='radiogroup'>
        {children}
      </tr>
    );
  } else {
    return <tr>{children}</tr>;
  }
};

const ChoiceCell = ({
  answer, answerIndex, isCorrectAnswer, isMultiChoice,
  isSelected, labelInCell, lessonElementId, lessonElementState,
  model, onSelected, promptId, readOnly, showAnswers,
  showFeedbackOnlyOnSelected = false
}) => {
  const id = `${promptId}-${answer}`;
  const inputType = isMultiChoice ? 'radio' : 'checkbox';
  const symbolType = `${inputType }-symbol`;
  const tabIndex = 0;

  let visualClass = 'choice-input';
  if (showAnswers) {
    if (isCorrectAnswer) {
      visualClass = 'correct choice-input';
    } else {
      visualClass = 'incorrect choice-input';
    }
  }

  const [focused, setFocused] = useState(false);

  const showFeedback = showFeedbackOnlyOnSelected ? isSelected : (isSelected || isCorrectAnswer);

  const callOnSelected = (data) => {
    const { checked } = data.target;
    onSelected(promptId, checked, answerIndex);
  };

  const renderFeedbackIcon = () => {
    return (
      <div className='correct-answer-symbol'>
        <FeedbackIcon
          isCorrectOverride={isSelected && isCorrectAnswer} isScored={lessonElementState.isScored}
          lessonElementId={lessonElementId} />
      </div>
    );
  };

  return (
    <td className='choice-matrix-cell'>
      {model.answerPosition === 'cells' && showFeedback && renderFeedbackIcon()}
      <label className='selector-container' htmlFor={id}>
        {model.answerPosition !== 'cells' && showFeedback && renderFeedbackIcon()}
        <div className='text-container'>
          {labelInCell ? <label htmlFor={id}><HtmlComponent htmlStr={answer} /></label> : ''}
          <label className={`symbol outline-separate ${readOnly ? 'disabled' : ''} ${isSelected ? 'selected' : ''} ${symbolType} ${visualClass} ${focused ? 'focused' : ''}`} htmlFor={id} />
          {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
          <input
            aria-checked={isSelected}
            checked={isSelected}
            className={visualClass} disabled={readOnly} id={id}
            name={promptId} onBlur={() => setFocused(false)} onChange={callOnSelected} onFocus={() => setFocused(true)}
            tabIndex={tabIndex} type={inputType} value={id} />
        </div>
      </label>
    </td>
  );
};

export default ChoiceMatrixMulti;
